import { useAuth0 } from '@auth0/auth0-react';
import useSWR from 'swr';
import useSWRMutation from 'swr/mutation';
import { ClientOverviewResponse } from '@/DTO/Clients/ClientOverviewResponse';
import { ClientResponse } from '@/DTO/Clients/ClientResponse';
import { CreateClientRequest } from '@/DTO/Clients/CreateClientRequest';
import useCompanyId from '@/Hooks/UseCompanyId';
import Page, { EmptyClientOverviewPage } from '@/Models/Page';
import http from '@/Utils/HTTP/NewHttpUtils';

const clientsOverviewKey = '/clients/overview';

const buildQuery = (offset: number, limit: number, search?: string | null) => {
    const query = new URLSearchParams({
        offset: offset.toString(),
        limit: limit.toString(),
    });

    if (search && search?.length > 1) {
        query.append('search', search);
    }

    return query.toString();
};

export const useDefaultClient = (companyId?: string) => {
    const { getAccessTokenSilently } = useAuth0();

    return useSWR(companyId ? `clients/company/${companyId}/default-client` : null, async (path) => {
        const result = await http.get<ClientOverviewResponse>({
            path,
            accessToken: await getAccessTokenSilently(),
        });

        return result;
    });
};

export const useClientsOverview = (offset: number, limit: number, search?: string | null) => {
    const { getAccessTokenSilently } = useAuth0();
    const companyId = useCompanyId();
    return useSWR(
        [clientsOverviewKey, companyId, offset, limit, search],
        async ([, companyId, offset, limit, search]) => {
            const result = await http.get<Page<ClientOverviewResponse>>({
                path: `clients/company/${companyId}/clients-overview/?${buildQuery(offset, limit, search)}`,
                accessToken: await getAccessTokenSilently(),
            });

            return result?.data ? result : EmptyClientOverviewPage;
        },
    );
};

export const useCreateClient = () => {
    const { getAccessTokenSilently } = useAuth0();
    const companyId = useCompanyId();
    return useSWRMutation(clientsOverviewKey, async (_, { arg }: { arg: CreateClientRequest }) => {
        const result = await http.post<ClientResponse>({
            path: `clients/company/${companyId}`,
            accessToken: await getAccessTokenSilently(),
            body: arg,
        });

        return result;
    });
};
